/**
 * Маппінг backend сутностей (з permissions) на frontend ресурси
 * Одна backend сутність може відповідати декільком frontend ресурсам
 */
export const ResourceCorrespondenceConfig = {
    // Запис boilerplate `List` (статті витрат і доходів, періоди, проєкти)
    // прибрано в S9a: таких ресурсів в Астеріксі немає, а ключа `List` бек не віддає.
    // Валюти й курси — Ship ядра (`SPEC-007` §9.3). Лише тут: ключа `List`
    // бек не віддає, і запис там нічого не відкривав
    CurrencyRate: 'Setting.ListCurrencyRate',
    Currency: 'Setting.ListCurrency',
    // Юридичні особи (`SPEC-007` §9.3): бек дає право бухгалтеру й
    // адміністратору, сторінку відкриває право сімейних довідників
    LegalEntity: 'Setting.ListLegalEntity',
    Locale: '?',
    Option: '?',
    SystemEvent: '?',
    TenantOption: '?',
    Tenant: '?',
    UserFavorite: '?',
    UserNotification: '?',
    UserOption: '?',
    UserPhotoFile: '?',
    User: 'Setting.User',
    Department: 'Setting.Department',
    // Сім'ї довідників (`SPEC-007` §9.3): один Rule — одна сім'я ресурсів
    StatusList: [
        'Setting.ListClientStatus',
        'Setting.ListCarrierStatus',
        'Setting.ListActivityStatus',
        'Setting.ListApplicationStatus',
        'Setting.ListRequestStatus',
        'Setting.ListTenderStatus',
    ],
    LossReasonList: [
        'Setting.ListApplicationLossReason',
        'Setting.ListRequestLossReason',
        'Setting.ListTenderLossReason',
    ],
    CrmList: [
        'Setting.ListActivityType',
        'Setting.ListCountry',
        'Setting.ListFormTaxation',
        'Setting.ListCarType',
        'Setting.ListCarOwnershipType',
        'Setting.ListCarParkSize',
        'Setting.ListBorderCrossing',
    ],
    // Типи документів (`SPEC-012` §3.4): ресурс у PrintableContainer
    DocumentType: 'Setting.ListDocumentType',
    // Шаблони листів (`SPEC-013` §3.4, S8a): ресурс у ListContainer, сторінку
    // відкриває право сімейних довідників
    LetterTemplate: 'Setting.ListLetterTemplate',
    // Клієнти (`SPEC-009` §9.3). Діти — під ключем власника: права контакту
    // й документа дає `ClientRule` (`CLIENT_UPDATE` на клієнта)
    Client: ['Crm.Client', 'Crm.ClientContact', 'Crm.ClientDocument'],
    // Договори клієнта — окремий ключ: їх веде бухгалтер, який клієнта не
    // змінює (`CLIENT_CONTRACT_*`, `SPEC-003` §2.4); «Додати договір» питає
    // колекційне `create` саме цього ключа
    ClientContract: 'Crm.ClientContract',
    // Перевізники (`SPEC-010` §3.4, S5a–S5b). Діти — під ключем власника:
    // права контакту й документа дає `CarrierRule` (`CARRIER_UPDATE` на
    // перевізника з правилом «лише логіст»)
    Carrier: ['Crm.Carrier', 'Crm.CarrierContact', 'Crm.CarrierDocument'],
    // Договори перевізника — окремий ключ: їх веде бухгалтер і старший логіст,
    // а не той, хто змінює перевізника (`CARRIER_CONTRACT_*`, `SPEC-003` §2.5);
    // «Додати договір» питає колекційне `create` саме цього ключа
    CarrierContract: 'Crm.CarrierContract',
    // Інші перевізники (`SPEC-010` §3.4, S5c): усе, включно з читанням, — лише
    // логістам (`OtherCarrierRule`); за `read_collection` цього ключа ховається
    // й пункт меню (`GATED_MENU_ITEMS`)
    OtherCarrier: 'Crm.OtherCarrier',
    // Справи (`SPEC-011` §10, S6a): «Нова справа» — колекційне `create`, пункт
    // меню й вкладка «Справи» в картках — `read_collection`; «бачить усі» —
    // `read_all` (адміністратор)
    Activity: 'Crm.Activity',
    // Листи (`SPEC-013` §11, S8b): вкладка «Листи» в картках — `read_collection`;
    // ядрового «Додати» немає (`create: false`, листи створює лише генерація),
    // права рядка — `_permissions` листа (`update`, `delete`, `send`)
    Letter: 'Communication.Letter',
    // Завдання — модуль з демки (`SPEC-018`, рішення замовника 2026-10-15).
    // Бекенд віддає глобальні права під ключем `Task`; фронт-ресурс має ключ `Task`.
    // Потрібно для CheckGlobalResourceAccessTask (create / read_collection списку завдань).
    Task: 'Task',
    // Запити (`SPEC-022` §8.1, §9, Н-022.2): «Додати» — колекційне `create`,
    // пункт меню й вкладка «Запити» в картці клієнта — `read_collection`
    // (K, M, L; «Фінанси» без них запитів не бачать, Q2.13)
    Request: 'Transportation.Request',
    // Тендери (`SPEC-023` §4.5, Н-023): те саме, що в запитів — пункт меню
    // й вкладка «Тендер» клієнта за `read_collection`
    Tender: 'Transportation.Tender',
    // Заявки (`SPEC-021` §8, §9, 021-Н4): реєстр, «Рейси» (той самий бек,
    // інша вибірка; «Додати» там гасить `CREATED_FROM_PARENT`) і документи —
    // правами заявки (`ApplicationRule`: документ веде той, хто змінює заявку)
    Application: ['Transportation.Application', 'Transportation.Trip', 'Transportation.ApplicationDocument'],
    // Оплати заявки — окремий ключ: їх веде «Фінанси» й логісти на будь-якій
    // видимій заявці (Q2.7), а не той, хто змінює заявку
    ApplicationPayment: 'Transportation.ApplicationPayment',
    // Цінові пропозиції (`SPEC-024` §5.2, Н-024.2): «Додати» в блоці картки
    // власника — колекційне `create` тієї ж відповіді, вибір автора —
    // колекційне `chooseResponsible` («лише логіст» вказує лише себе, ПР-02)
    RequestOffer: 'Transportation.RequestOffer',
    TenderOffer: 'Transportation.TenderOffer',
    ApplicationOffer: 'Transportation.ApplicationOffer',
    // Звіти (`SPEC-027` §3.4, §6, Н-027.4): контейнер-читач без ресурсів,
    // ключ прав `Report` бек віддає кожному співробітнику (`read`); рядки
    // звітів обмежує бек (`logisticians_all`, `client_losses_all`)
    Report: ['Report.Index', 'Report.LogisticiansFinancial', 'Report.LogisticiansInvolvement', 'Report.ClientLosses'],
    // Адміністрування диска (`DISK-006` §2.1, ADR 052): контейнер-читач без
    // ресурсів; ключ `DiskAdmin` (`DiskAdminRule::ACCESS`) бек віддає лише
    // адміністратору — за `read_collection` видно пункт меню й сторінки панелі
    DiskAdmin: 'Disk.Admin',
}

// Кешований обернений маппінг
let cachedRevertedConfig: Record<string, string> | null = null

/**
 * Реверсний маппінг: frontend ресурс -> backend сутність
 * Використовується CheckGlobalResourceAccessTask для перевірки доступів
 */
export const RevertResourceCorrespondenceConfig = (() => {
    if (cachedRevertedConfig) {
        return cachedRevertedConfig
    }

    const reverted: Record<string, string> = {}

    for (const [key, value] of Object.entries(ResourceCorrespondenceConfig)) {
        // Пропускаємо елементи зі значенням "?"
        if (value === '?') {
            continue
        }

        // Обробка масиву (наприклад List)
        if (Array.isArray(value)) {
            for (const item of value) {
                reverted[item] = key
            }
        } else {
            // Звичайне значення
            reverted[value] = key
        }
    }

    cachedRevertedConfig = reverted
    return reverted
})()
